/* ============================================================
   PDP — behaviour
   Scoped to [data-ms-pdp]. No globals leak, no Platter code touched.
   ============================================================ */
(function () {
  'use strict';

  function money(cents) {
    var fmt = window.msMoneyFormat || '${{amount}}';
    var amount = (cents / 100);
    var withDecimals = amount.toFixed(2);
    var noDecimals = Math.round(amount).toString();
    function group(str) {
      var parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, group(noDecimals))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, group(withDecimals).replace(/,/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, group(noDecimals))
      .replace(/\{\{\s*amount\s*\}\}/g, group(withDecimals));
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function buyForm(root) {
    /* the buy form is emitted by the Liquid form tag, which won't carry a
       custom data attribute — fall back to its class */
    return root.querySelector('[data-ms-form]') || root.querySelector('form.ms-cta');
  }

  /* ---------------- variant picker ---------------- */
  function initVariants(root) {
    var form = buyForm(root);
    if (!form) return;

    var variants = readJSON(root, '[data-ms-variants]') || [];
    if (!variants.length) return;

    var idInput = form.querySelector('[data-ms-variant-id]');
    var atc = form.querySelector('[data-ms-atc]');
    var atcText = form.querySelector('[data-ms-atc-text]');
    var priceEl = root.querySelector('[data-ms-price]');
    var compareEl = root.querySelector('[data-ms-compare]');
    var offEl = root.querySelector('[data-ms-off]');
    var groups = Array.prototype.slice.call(form.querySelectorAll('[data-ms-option]'));
    var buttons = Array.prototype.slice.call(form.querySelectorAll('[data-ms-swatch]'));
    if (!buttons.length) return;

    var soldOutLabel = (atcText && atcText.getAttribute('data-soldout')) || 'Sold Out';
    var inStockLabel = (atcText && atcText.getAttribute('data-instock')) || 'Add To Cart';

    /* selection is indexed by the product's real option position */
    var selection = [];
    buttons.forEach(function (btn) {
      var i = parseInt(btn.getAttribute('data-option-index'), 10);
      if (btn.getAttribute('aria-pressed') === 'true') selection[i] = btn.getAttribute('data-value');
    });

    function variantOptions(v) {
      return [v.option1, v.option2, v.option3];
    }

    function matches(v, sel) {
      var opts = variantOptions(v);
      for (var i = 0; i < sel.length; i++) {
        if (sel[i] == null) continue;
        if (opts[i] !== sel[i]) return false;
      }
      return true;
    }

    function findVariant() {
      for (var i = 0; i < variants.length; i++) {
        if (matches(variants[i], selection)) return variants[i];
      }
      return null;
    }

    /* cross out combinations that don't exist / are out of stock */
    function refreshAvailability() {
      buttons.forEach(function (btn) {
        var i = parseInt(btn.getAttribute('data-option-index'), 10);
        var probe = selection.slice();
        probe[i] = btn.getAttribute('data-value');
        var possible = variants.some(function (v) {
          return matches(v, probe) && v.available;
        });
        btn.setAttribute('data-unavailable', possible ? 'false' : 'true');
      });
    }

    function render() {
      var v = findVariant();
      refreshAvailability();

      groups.forEach(function (g) {
        var i = parseInt(g.getAttribute('data-option-index'), 10);
        var label = g.querySelector('[data-ms-option-value]');
        if (label) label.textContent = selection[i] || '';
      });

      if (!v) {
        if (atc) atc.disabled = true;
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }

      if (idInput) idInput.value = v.id;

      if (priceEl) priceEl.textContent = money(v.price);
      if (compareEl && offEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = money(v.compare_at_price);
          compareEl.hidden = false;
          offEl.textContent = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '% off';
          offEl.hidden = false;
        } else {
          compareEl.hidden = true;
          offEl.hidden = true;
        }
      }

      if (atc) atc.disabled = !v.available;
      if (atcText) atcText.textContent = v.available ? inStockLabel : soldOutLabel;

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }

      root.dispatchEvent(new CustomEvent('ms:variant:change', { bubbles: true, detail: { variant: v } }));
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-option-index'), 10);
        selection[i] = btn.getAttribute('data-value');
        buttons.forEach(function (other) {
          if (parseInt(other.getAttribute('data-option-index'), 10) === i) {
            other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
          }
        });
        render();
      });
    });

    render();
  }

  /* ---------------- add to cart ----------------
     The buy box is a Liquid form tag, so submitting it does a full page
     POST and lands on /cart — the drawer never opens because nothing ever
     told it to.

     Platter's own add-to-cart buttons are Alpine components calling
     $data.handleAddToCart, which this section sits outside of. The two
     globals that handler ultimately uses are reachable from anywhere, and
     assets/__block--add-products-to-cart.js shows the exact contract:

         const data = await _cart.add({ items });
         if (!data.cart_error) _stores.modal.setId("cart-drawer");

     _cart.add performs the AJAX add and updates the reactive cart state
     that the drawer contents and the header count both read from, so the
     drawer opens already populated. Same two calls, same order, here.

     If either global is missing — script order changed, Platter upgraded,
     JS half-loaded — the handler returns before preventDefault and the
     native POST runs exactly as it does today. A shopper gets a redirect
     to /cart rather than a button that silently does nothing, which is the
     right way for this to fail.
     ---------------- */
  function initAddToCart(root) {
    var form = buyForm(root);
    if (!form) return;

    var atc = form.querySelector('[data-ms-atc]');
    var atcText = form.querySelector('[data-ms-atc-text]');

    function label(name, fallback) {
      return (atcText && atcText.getAttribute(name)) || fallback;
    }

    form.addEventListener('submit', function (e) {
      var cart = window._cart;
      var stores = window._stores;

      /* no Platter cart API -> let the browser post the form */
      if (!cart || typeof cart.add !== 'function') return;

      var idInput = form.querySelector('[data-ms-variant-id]');
      var id = idInput && idInput.value;
      if (!id) return;

      e.preventDefault();

      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (!qty || qty < 1) qty = 1;

      var item = { id: id, quantity: qty };

      /* carry any line-item properties the form is collecting */
      var props = {};
      var hasProps = false;
      Array.prototype.slice.call(form.querySelectorAll('[name^="properties["]')).forEach(function (el) {
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        if (!el.value) return;
        var open = el.name.indexOf('[');
        var close = el.name.lastIndexOf(']');
        if (open < 0 || close <= open) return;
        props[el.name.slice(open + 1, close)] = el.value;
        hasProps = true;
      });
      if (hasProps) item.properties = props;

      var idle = atcText ? atcText.textContent : '';

      function settle(text) {
        form.classList.remove('is-adding');
        if (atc) atc.disabled = false;
        if (atcText) atcText.textContent = text;
      }

      function failed() {
        settle(label('data-error', 'Try again'));
        window.setTimeout(function () {
          if (atcText) atcText.textContent = idle;
        }, 2200);
      }

      form.classList.add('is-adding');
      if (atc) atc.disabled = true;
      if (atcText) atcText.textContent = label('data-adding', 'Adding\u2026');

      Promise.resolve(cart.add({ items: [item] })).then(function (data) {
        if (data && data.cart_error) {
          failed();
          return;
        }
        settle(idle);
        if (stores) {
          if (stores.modal && typeof stores.modal.setId === 'function') {
            stores.modal.setId('cart-drawer');
          }
          /* mirror Platter's own handler: a quick-view or product drawer
             sitting open would otherwise stack on top of the cart */
          if (stores.productDrawer) stores.productDrawer.open = false;
          if (stores.quickView) stores.quickView.open = false;
        }
        root.dispatchEvent(new CustomEvent('ms:cart:added', {
          bubbles: true,
          detail: { item: item }
        }));
      }).catch(failed);
    });
  }

  /* ---------------- gallery dots (mobile carousel) ---------------- */
  function initGallery(root) {
    var track = root.querySelector('[data-ms-gal-track]');
    var dotsWrap = root.querySelector('[data-ms-gal-dots]');
    if (!track || !dotsWrap) return;

    var cells = Array.prototype.slice.call(track.querySelectorAll('[data-ms-gal-cell]'));
    if (cells.length < 2) return;

    dotsWrap.innerHTML = '';
    cells.forEach(function (cell, i) {
      var dot = document.createElement('span');
      dot.className = 'ms-gal__dot';
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      dotsWrap.appendChild(dot);
    });
    var dots = Array.prototype.slice.call(dotsWrap.children);

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var active = 0;
        cells.forEach(function (cell, i) {
          if (cell.offsetLeft <= mid && cell.offsetLeft + cell.offsetWidth > mid) active = i;
        });
        dots.forEach(function (d, i) { d.setAttribute('aria-current', i === active ? 'true' : 'false'); });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------------- accordions (all start collapsed) ---------------- */
  function initAccordions(root) {
    root.querySelectorAll('.ms-acc__btn').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ---------------- modals ---------------- */
  function initModals(root) {
    var lastFocus = null;

    function open(name) {
      var modal = root.querySelector('[data-ms-modal="' + name + '"]');
      if (!modal) return;
      lastFocus = document.activeElement;
      modal.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      var closeBtn = modal.querySelector('.ms-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    function closeAll() {
      root.querySelectorAll('.ms-modal[open]').forEach(function (m) { m.removeAttribute('open'); });
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    root.querySelectorAll('[data-ms-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { open(btn.getAttribute('data-ms-open')); });
    });
    root.querySelectorAll('[data-ms-close]').forEach(function (el) {
      el.addEventListener('click', closeAll);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* ---------------- size chart ---------------- */
  function initSizeChart(root) {
    var body = root.querySelector('[data-ms-size-body]');
    var data = readJSON(root, '[data-ms-size-json]');
    if (!body || !data || !data.data) return;

    var chart = data.data;
    var columns = chart.columns || [];
    var rows = chart.rows || [];
    var units = chart.options && chart.options.length ? chart.options : ['inch'];
    var unit = units[0];

    function valuesFor(row, u) {
      var keys = row.keys || {};
      var entry = keys[u];
      if (!entry || !entry.values) return [];
      var vals = entry.values.slice();
      /* some rows carry a leading empty cell so the array is 1 longer
         than the column list — trim it so cells line up with headers */
      while (vals.length > columns.length && vals[0] === '') vals.shift();
      return vals;
    }

    function renderNotes() {
      if (!data.text) return '';
      var html = '<div class="ms-fitnotes">';
      data.text.split('\n').forEach(function (line) {
        var t = line.trim();
        if (!t) return;
        if (t.indexOf('###') === 0) {
          html += '<h3>' + t.replace(/^#+\s*/, '') + '</h3>';
        } else {
          html += '<p>' + t + '</p>';
        }
      });
      return html + '</div>';
    }

    function draw() {
      var html = '';

      if (units.length > 1) {
        html += '<div class="ms-units">';
        units.forEach(function (u) {
          html += '<button type="button" data-ms-unit="' + u + '" aria-pressed="' +
                  (u === unit ? 'true' : 'false') + '">' + u + '</button>';
        });
        html += '</div>';
      }

      html += '<div class="ms-table-wrap"><table class="ms-table"><thead><tr><th>' +
              (chart.rowTitle || '') + '</th>';
      columns.forEach(function (c) { html += '<th>' + c + '</th>'; });
      html += '</tr></thead><tbody>';

      rows.forEach(function (row) {
        var vals = valuesFor(row, unit);
        html += '<tr><th>' + (row.title || '') + '</th>';
        columns.forEach(function (c, i) {
          html += '<td>' + (vals[i] != null && vals[i] !== '' ? vals[i] : '\u2014') + '</td>';
        });
        html += '</tr>';
      });

      html += '</tbody></table></div>' + renderNotes();
      body.innerHTML = html;

      body.querySelectorAll('[data-ms-unit]').forEach(function (b) {
        b.addEventListener('click', function () {
          unit = b.getAttribute('data-ms-unit');
          draw();
        });
      });
    }

    draw();
  }

  function init() {
    document.querySelectorAll('[data-ms-pdp]').forEach(function (root) {
      if (root.hasAttribute('data-ms-ready')) return;
      root.setAttribute('data-ms-ready', '');
      initVariants(root);
      initAddToCart(root);
      initGallery(root);
      initAccordions(root);
      initModals(root);
      initSizeChart(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* re-init when the theme editor re-renders the section */
  document.addEventListener('shopify:section:load', init);
})();
